import TicketSaleCard from 'app/components/TicketSaleCard/TicketSaleCard';
import { DateTime } from 'luxon';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { clearCartData } from 'store/cartSlice';
import { selectCartData } from 'store/cartSlice/selectors';
import {
  removeAllTheSelectedItems,
  removeQueryParams,
  resetVoucherData,
  setAllTheSelectedItems,
  setQueryParams,
} from '../TicketSalePage/slice';
import {
  makeSelectedEvent,
  selectSelectedTickets,
  selectTicketIdsFromQueryParams,
} from '../TicketSalePage/slice/selectors';

import TicketSalePageHeader from 'app/components/TicketSalePageHeader/TicketSalePageHeader';
import TicketSalePageMeta from '../TicketSalePage/TicketSalePageMeta';
import useToastError from '../TicketSalePage/useToastError';
import useViewItemListGTM from '../TicketSalePage/useViewItemListGTM';
import useEventWithTickets from '../TicketSalePage/useEventWithTickets';
import useChatBase from './useChatBase';
import CartCountdownTimerSkeleton from 'app/components/Skeletons/Common/CartCountdownTimer';
import CartStepsSkeleton from 'app/components/Skeletons/Common/CartStepsSkeleton';
import CartStepCardSkeleton from 'app/components/Skeletons/Common/CartStepCardSkeleton';

const CalendarPage: React.FC<{}> = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const selectedEvent = useSelector(makeSelectedEvent);
  const selectedTickets = useSelector(selectSelectedTickets);
  const {
    loading: cartIsLoading,
    selectedEvent: cartEvent,
    selectedEvent: { id: cartEventId },
    selectedItems: cartItems,
    countdown: { endTime },
  } = useSelector(selectCartData);
  const ticketIdsFromQueryParamsStore = useSelector(
    selectTicketIdsFromQueryParams,
  );

  useEventWithTickets({
    extraIncludes: ['calendar', 'reservedTickets', 'ticketTypeBundles'],
  });

  useToastError();

  useViewItemListGTM(selectedEvent);

  useEffect(() => {
    let secondsRemaining;
    if (endTime) {
      secondsRemaining = Math.round(
        DateTime.fromISO(endTime).diffNow('seconds').seconds,
      );
    } else {
      secondsRemaining = 0;
    }
    // remove persisted data if there is a cart and it is expired
    if (secondsRemaining <= 0 && cartEventId) {
      dispatch(clearCartData());
    } else if (cartEventId && selectedEvent.id !== cartEventId) {
      // remove selected tickets & voucher if there is an ongoing cart with another event
      dispatch(removeAllTheSelectedItems());
      dispatch(resetVoucherData());
    } else if (
      cartEventId === selectedEvent.id &&
      cartItems.length > 0 &&
      selectedTickets.length === 0
    ) {
      // automatically select tickets if there is an ongoing cart with the current event
      dispatch(setAllTheSelectedItems({ cartItems, cartEvent }));
    }

    const ticketIdsFromQueryParams = searchParams.get('tickets');

    if (ticketIdsFromQueryParams) {
      dispatch(setQueryParams(ticketIdsFromQueryParams));
    } else if (ticketIdsFromQueryParamsStore) {
      dispatch(removeQueryParams());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent.id]);

  useChatBase();

  return (
    <div className="page-container calendar-page">
      <TicketSalePageMeta />
      {cartIsLoading ? (
        <>
          <CartCountdownTimerSkeleton />
          <div className="section-container">
            <CartStepsSkeleton />
            <CartStepCardSkeleton />
          </div>
        </>
      ) : (
        <>
          <TicketSalePageHeader className="ml-4 mt-4 lg:absolute" />
          <div className="section-container">
            <TicketSaleCard />
          </div>
        </>
      )}
    </div>
  );
};

export default CalendarPage;
